import { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface FormCEPProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  validationError?: string;
  required?: boolean;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const formatCEP = (value: string): string => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length >= 8) {
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  }
  return numbers;
};

const FormCEP = forwardRef<HTMLInputElement, FormCEPProps>(
  ({ className, error, validationError, required, value, onChange, ...props }, ref) => {
    const [internalValue, setInternalValue] = useState(value);

    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInternalValue(e.target.value);
      onChange(e);
    };

    const handleBlur = () => {
      const formattedValue = formatCEP(internalValue);
      setInternalValue(formattedValue);
      onChange({
        target: { name: props.name || '', value: formattedValue },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const inputClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <input
          ref={ref}
          type="text"
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          pattern="\d{5}-\d{3}"
          title="Digite um CEP válido no formato 00000-000"
          className={inputClassName}
          required={required}
          {...props}
        />
      </div>
    );
  }
);

FormCEP.displayName = 'FormCEP';

export { FormCEP };
